import { ThreadPool } from "@mm/threading-lib";
import type {
  MediaExportPool_MsgToPool,
  MediaExportPool_MsgToWorkerThread,
  Snapshot,
} from "./MediaExportThreadPoolMsg";
import type { DOMWindowDimention } from "./MediaManager/video-resolution/videoResolution";

const MAX_SNAPSHOT_THREADS = 4;
const MIN_SNAPSHOT_THREADS = 2;
const DEFAULT_HARDWARE_CONCURRENCY = 4;

function getSnapshotThreadCount() {
  const hardwareConcurrency =
    typeof navigator !== "undefined" && typeof navigator.hardwareConcurrency === "number"
      ? navigator.hardwareConcurrency
      : DEFAULT_HARDWARE_CONCURRENCY;
  const ideal = Math.max(MIN_SNAPSHOT_THREADS, hardwareConcurrency - 2);
  return Math.max(1, Math.min(MAX_SNAPSHOT_THREADS, ideal));
}

function isFailureMessage(msg: MediaExportPool_MsgToPool): msg is MediaExportPool_MsgToPool & { isFailure: true } {
  return (msg as { isFailure?: boolean }).isFailure === true;
}

abstract class MediaExportWorkerPoolBase extends ThreadPool<
  MediaExportPool_MsgToWorkerThread,
  MediaExportPool_MsgToPool
> {
  newThread(_threadId: number): Worker {
    return new Worker(new URL("./MediaExportThreadPool.worker", import.meta.url), { type: "module" });
  }

  protected rejectIfFailure(
    query: { resolve: (value: any) => void; reject: (reason?: any) => void },
    msg: MediaExportPool_MsgToPool,
  ): boolean {
    if (isFailureMessage(msg)) {
      query.reject(msg.payload);
      return true;
    }
    return false;
  }
}

class SnapshotThreadPool extends MediaExportWorkerPoolBase {
  constructor() {
    super(getSnapshotThreadCount());
  }

  multiplex(
    query: { resolve: (value: any) => void; reject: (reason?: any) => void },
    msg: MediaExportPool_MsgToPool,
  ): void {
    if (this.rejectIfFailure(query, msg)) {
      return;
    }

    switch (msg.kind) {
      case "MsgAnswer_CreateSnapshot":
        query.resolve(msg.payload.snapshot);
        break;
      default:
        query.reject(new Error(`Unexpected snapshot response: ${msg.kind}`));
        break;
    }
  }

  createSnapshot(snapshots: Snapshot[], domWindowDimention: DOMWindowDimention) {
    const transferable = snapshots
      .filter(({ snapshot }) => snapshot instanceof ImageBitmap)
      .map((snapshot) => snapshot.snapshot as ImageBitmap);

    return this.send<Blob>(
      {
        kind: "Msg_CreateSnapshot",
        payload: {
          snapshots,
          domWindowDimention,
        },
      },
      transferable,
    );
  }
}

class VideoThreadPool extends MediaExportWorkerPoolBase {
  constructor() {
    super(1);
  }

  multiplex(
    query: { resolve: (value: any) => void; reject: (reason?: any) => void },
    msg: MediaExportPool_MsgToPool,
  ): void {
    if (this.rejectIfFailure(query, msg)) {
      return;
    }

    switch (msg.kind) {
      case "MsgAnswer_CreateVideo":
      case "MsgAnswer_PushFrame":
      case "MsgAnswer_DisposeVideo":
        query.resolve(undefined);
        break;
      case "MsgAnswer_ProcessVideo":
        query.resolve(msg.payload.data);
        break;
      default:
        query.reject(new Error(`Unexpected video response: ${msg.kind}`));
        break;
    }
  }

  createVideo(domWindowDimention: DOMWindowDimention, fps: number) {
    return this.send<void>({
      kind: "Msg_CreateVideo",
      payload: {
        domWindowDimention,
        fps,
      },
    });
  }

  pushFrame(snapshots: Snapshot[]) {
    const transferable = snapshots
      .filter(({ snapshot }) => snapshot instanceof ImageBitmap)
      .map((snapshot) => snapshot.snapshot as ImageBitmap);

    return this.send<void>(
      {
        kind: "Msg_PushFrame",
        payload: {
          snapshots,
        },
      },
      transferable,
    );
  }

  processVideo() {
    return this.send<ArrayBuffer>({
      kind: "Msg_ProcessVideo",
      payload: {},
    });
  }

  disposeVideo() {
    return this.send<void>({
      kind: "Msg_DisposeVideo",
      payload: {},
    });
  }
}

export class MediaExportThreadPool {
  private snapshotPool = new SnapshotThreadPool();
  private videoPool = new VideoThreadPool();

  createSnapshot(snapshots: Snapshot[], domWindowDimention: DOMWindowDimention) {
    return this.snapshotPool.createSnapshot(snapshots, domWindowDimention);
  }

  createVideo(domWindowDimention: DOMWindowDimention, fps: number) {
    return this.videoPool.createVideo(domWindowDimention, fps);
  }

  pushFrame(snapshots: Snapshot[]) {
    return this.videoPool.pushFrame(snapshots);
  }

  processVideo() {
    // Finalizes the current video session and releases worker-side encoder/canvas resources.
    return this.videoPool.processVideo();
  }

  disposeVideo() {
    // Explicit cancellation/early-cleanup path. Call when an export is aborted before finalize.
    return this.videoPool.disposeVideo();
  }
}

export const mediaExportThreadPool = new MediaExportThreadPool();
